import { AgGridReact } from 'ag-grid-react';
import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { H2 } from '../../../../common/Headings/Headings.styles';
import Button from '../../../../components/Button/Button';
import { Variant } from '../../../../components/Button/Button.types';
import Table from '../../../../components/Table/Table';
import { Organization } from '../../../../services/menu/menu.types';
import { Buttons, SelectMenusPage, TableWrapper, TableRowWrapper } from './SelectMenus.styles';
import { SelectMenusProps } from './SelectMenus.types';
import MenuTreeView from '../../../../components/MenuTreeView/MenuTreeView';
import Checkbox from '../../../../components/Checkbox/Checkbox';
import { StyledSubHeader, StyledSubTitle } from '../../../Extract/Menu/SelectMenus/SelectMenus.styles';
import { StyledFooter, StyledFooterText, StyledHeaderTitle } from '../../../../components/Table/Table.styles';
import { useMenusWithSpecNos } from '../../../../services/menu/menu.service';
import { BuildRequestBodyParams } from '../../../../components/MenuTreeView/MenuTreeView.types';
import isEqual from 'react-fast-compare';
import useTreeViewRowStates from '../../../../components/MenuTreeView/useMenuTreeViewRowStates';
import { RowSelectedEvent } from 'ag-grid-community';
import {
  FiltersWrapper,
  HeaderInputButton,
  HeaderWrapper,
  StyledHeader,
} from '../../BulkEditMenu/SelectMenu/SelectMenu.styles';
import Radio from '../../../../components/Radio/Radio';
import InputButton from '../../../../components/InputButton/InputButton';
import { ChangeSpecsStep } from '../ChangeSpecs.types';

const SelectMenus: FC<SelectMenusProps> = ({ selectedMenus, setSelectedMenus, selectedSpecs, setStep }) => {
  const { data: orgs, isLoading: isOrgLoading } = useMenusWithSpecNos(
    { specNos: selectedSpecs.map((combinedSpec) => combinedSpec.currentSpecNo.toString()).join(',') },
    true
  );
  const availableOrgs = orgs?.data || [];

  const gridRef = useRef<AgGridReact>(null);
  const filterRef = useRef<HTMLInputElement>(null);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [displayedRowCount, setDisplayedRowCount] = useState<number>(0);
  const [tableUpdate, setTableUpdate] = useState<boolean>(false);
  const [focusEnabled, setFocusEnabled] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [radioFilter, setRadioFilter] = useState<string>('');
  const [filterId, setFilterId] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const translateFilter: any = {
    org: 'Market',
    store: 'Store',
  };

  useEffect(() => {
    let count = 0;
    let menuList: Organization[] = [];
    if (availableOrgs !== undefined) {
      if (availableOrgs.length > 0) {
        availableOrgs.forEach((org) => {
          if (org.children !== undefined) {
            org.children.forEach((store) => {
              count++;
              if (store.children !== undefined) {
                store.children.forEach((menu) => {
                  menuList.push(menu);
                });
              }
            });
          }
        });
      }
      let filteredMenus = selectedMenus.filter((selectedMenu) => {
        let isPresentOnLoadedMenus = menuList.some((menu) => selectedMenu.id === menu.id);
        if (isPresentOnLoadedMenus) {
          handleCheckboxClick(selectedMenu, true);
        }
        return isPresentOnLoadedMenus;
      });
      setSelectedMenus(filteredMenus);
    }
    setDisplayedRowCount(count);
  }, [availableOrgs]);

  useEffect(() => {
    gridRef?.current?.api?.selectAll();
  }, [selectedMenus, gridRef?.current?.api]);

  const handleSearchMenuInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(event.target.value);
  };

  const handleClear = () => {
    setSearchValue('');
    setFilterId('');
    setErrorMessage(undefined);
  };

  const dataStructure = {
    org: {
      idKey: 'org',
      showCheckboxes: true,
      buildLabel: (params: BuildRequestBodyParams) => params.row.label,
      buildParentArray: (params: Organization) => {
        return [{ level: 0, id: params.id }];
      },
      getIndexByType: () => {
        return 0;
      },
    },
    store: {
      idKey: 'store',
      showCheckboxes: true,
      buildLabel: (params: BuildRequestBodyParams) => params.row.label,
      buildParentArray: (params: Organization) => {
        const parents = [];
        if (params.parents) {
          parents.push({ level: 0, id: params.parents['org'] });
        }
        parents.push({ level: 1, id: params.id });
        return parents;
      },
      getIndexByType: () => {
        return 1;
      },
    },
    menu: {
      to: () => '',
      idKey: 'menuNo',
      showCheckboxes: true,
      buildLabel: (params: BuildRequestBodyParams) => {
        return `${params.row.label}`;
      },
      buildParentArray: (params: Organization) => {
        const parents = [];
        if (params.parents) {
          parents.push({ level: 0, id: params.parents['org'] });
          parents.push({ level: 1, id: params.parents['store'] });
        }
        parents.push({ level: 2, id: params.id });
        return parents;
      },
      getIndexByType: () => {
        return 2;
      },
    },
  };

  const handleCheckboxClickInternal = (row: Organization, rowChecked: boolean) => {
    if (rowChecked) {
      if (row.type === 'menu') {
        // menu row was checked
        setSelectedMenus((selectedMenu) => {
          const selectedMenusWithCheckedRowAdded = [...selectedMenu, row];
          const onlyUniqueValues = selectedMenusWithCheckedRowAdded.filter((menu, index) => {
            const firstIndex = selectedMenusWithCheckedRowAdded.findIndex((val) => val.id === menu.id);
            return index === firstIndex;
          });
          return onlyUniqueValues.map((menu) => {
            return {
              ...menu,
            };
          });
        });
      } else {
        traverseTree(row);
      }
    } else {
      // row was just unchecked
      setSelectedMenus((selectedMenus) => {
        // filter through selectedMenus and remove all the menus that correspond with that row
        return selectedMenus.filter((menu) => {
          let isRowAndParentEqual;
          if (menu.type === row.type) {
            isRowAndParentEqual = isEqual(row.id, menu.id) && isEqual(row.parents, menu.parents);
          } else {
            isRowAndParentEqual = isEqual(row.id, menu.parents ? menu.parents[row.type] : undefined);
          }
          return !isRowAndParentEqual;
        });
      });
      handleUpwardsDeselectPropagation(row);
    }
    // call the fn from useTreeViewRowStates to mark the row as checked/unchecked there
    handleCheckboxClick(row, rowChecked);
  };

  const traverseTree = (row: Organization) => {
    if (row.type === 'menu') {
      setSelectedMenus((selectedMenu) => {
        const selectedMenusWithCheckedRowAdded = [...selectedMenu, row];
        const onlyUniqueValues = selectedMenusWithCheckedRowAdded.filter((menu, index) => {
          const firstIndex = selectedMenusWithCheckedRowAdded.findIndex((val) => val.id === menu.id);
          return index === firstIndex;
        });
        return onlyUniqueValues.map((menu) => {
          return {
            ...menu,
            rowChecked: true,
          };
        });
      });
      handleCheckboxClick(row, true);
    } else if (row.children !== undefined) {
      row.children.forEach((childRow) => traverseTree(childRow));
    }
  };

  const {
    checkedRows,
    expandedRows,
    handleCheckboxClick,
    handleRowClick,
    handleUpwardsDeselectPropagation,
    resetCheckedRows,
    handleExternalTableDeselectPropagation,
  } = useTreeViewRowStates(dataStructure, 'selectMenusToChangeSpec', false);

  const searchForMenusInTree = (row: Organization) => {
    if (row.type === 'menu') {
      if (searchInputValue === row.id) {
        setSelectedMenus((selectedMenu) => {
          const selectedMenusWithCheckedRowAdded = [...selectedMenu, row];
          const onlyUniqueValues = selectedMenusWithCheckedRowAdded.filter((menu, index) => {
            const firstIndex = selectedMenusWithCheckedRowAdded.findIndex((val) => val.id === menu.id);
            return index === firstIndex;
          });
          return onlyUniqueValues.map((menu) => {
            return {
              ...menu,
              rowChecked: true,
            };
          });
        });
        handleCheckboxClick(row, true);
      }
    } else if (row.children !== undefined) {
      row.children.forEach((childRow) => searchForMenusInTree(childRow));
    }
  };

  const handleAddMenuClick = () => {
    availableOrgs?.forEach((org) => {
      searchForMenusInTree(org);
    });
  };

  const handleSelectAll = () => {
    if (selectedMenus.length === displayedRowCount && !isOrgLoading) {
      resetCheckedRows();
      setSelectedMenus([]);
    } else if (availableOrgs !== undefined) {
      let allMenus: Organization[] = [];
      availableOrgs.forEach((org) => {
        handleCheckboxClick(org, true);
        if (org.children !== undefined) {
          org.children.forEach((store) => {
            handleCheckboxClick(store, true);
            if (store.children !== undefined) {
              store.children.forEach((menu) => {
                allMenus.push(menu);
                handleCheckboxClick(menu, true);
              });
            }
          });
        }
      });
      setSelectedMenus(allMenus);
    }
  };

  const handleRowSelected = (event: RowSelectedEvent) => {
    if (event.node.isSelected() === false) {
      const newSelectedMenus = selectedMenus.filter((menu) => menu.id !== event.data.id);
      setSelectedMenus(newSelectedMenus);
      handleCheckboxClick(event.data, false);
      handleExternalTableDeselectPropagation(event.data);
      gridRef.current?.api.selectAll();
      setTableUpdate(!tableUpdate);
    }
  };

  const handleSearch = () => {
    setErrorMessage(undefined);
    setFilterId(() => '');
    let isRowFound = false;
    availableOrgs?.forEach((org) => {
      if (org.children !== undefined) {
        org.children.forEach((store) => {
          if (radioFilter === 'store' && store.id === searchValue) {
            setFilterId(store.id);
            handleRowClick(org, true);
            handleCheckboxClickInternal(store, true);
            isRowFound = true;
            return;
          }
          if (store.children !== undefined) {
            store.children.forEach((menu) => {
              if (searchValue.toLowerCase() === menu.additionalProperties?.marketNo?.toLowerCase()) {
                setFilterId(org.id);
                isRowFound = true;
                return;
              }
            });
          }
        });
      }
    });
    setFocusEnabled(!focusEnabled);
    if (!isRowFound) {
      setErrorMessage(`${translateFilter[radioFilter]} not found`);
    }
  };

  const handleRadioValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRadioFilter(event.target.value);
    handleClear();
  };
  const handleSearchValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    setErrorMessage(undefined);
    setFilterId('');
    setSearchValue(event.target.value);
  };

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSearchMenuInTree = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleAddMenuClick();
    }
  };

  const handleBackClick = () => {
    setStep(ChangeSpecsStep.SelectSpec);
  };

  return (
    <SelectMenusPage>
      <H2>Select menus to update</H2>
      <TableRowWrapper>
        <TableWrapper>
          <StyledHeader data-testid='header'>
            <HeaderWrapper>
              <StyledHeaderTitle>Available Store Numbers</StyledHeaderTitle>
            </HeaderWrapper>
            <FiltersWrapper>
              <StyledSubTitle>Search by:</StyledSubTitle>
              <Radio
                checked={radioFilter === 'org'}
                value='org'
                onChange={handleRadioValueChange}
                id={'marketNo'}
                label={'Market'}
              />
              <Radio
                checked={radioFilter === 'store'}
                value='store'
                onChange={handleRadioValueChange}
                id={'storeNo'}
                label={'Store'}
              />
              <HeaderInputButton>
                <InputButton
                  buttonProps={{
                    label: 'Search',
                    onClick: handleSearch,
                    testId: 'SearchButton',
                    disabled: searchValue === '',
                  }}
                  inputProps={{
                    onChange: handleSearchValue,
                    id: 'searchFilters',
                    label: '',
                    value: searchValue,
                    disabled: radioFilter === '',
                    onKeyDown: handleKeyDown,
                    testId: 'FilterInput',
                    maxLength: 20,
                    ref: filterRef,
                    errorText: errorMessage,
                  }}
                />
              </HeaderInputButton>
            </FiltersWrapper>
          </StyledHeader>
          <StyledSubHeader data-testid='subtitle'>
            <div style={{ marginLeft: '48px' }}>
              <Checkbox
                type='checkbox'
                checked={selectedMenus.length > 0 && selectedMenus.length === displayedRowCount}
                id={'select-all'}
                testId={'selectAll'}
                aria-checked={selectedMenus.length > 0 && selectedMenus.length === displayedRowCount}
                aria-disabled={'false'}
                readOnly
                indeterminate={selectedMenus.length > 0 && selectedMenus.length < displayedRowCount}
                label={<StyledSubTitle>Organizations</StyledSubTitle>}
                onClick={handleSelectAll}
              />
            </div>
          </StyledSubHeader>
          <MenuTreeView
            rows={availableOrgs ?? []}
            dataStructure={dataStructure}
            checkedRows={checkedRows}
            expandOnCheck={false}
            filterId={filterId}
            radioFilter={radioFilter}
            expandedRows={expandedRows}
            onRowClick={handleRowClick}
            onCheckboxClick={handleCheckboxClickInternal}
            defaultHandleCheckboxClick={handleCheckboxClick}
            isLoading={isOrgLoading}
            selectedRows={selectedMenus}
            externalTableUpdate={tableUpdate}
            height={'500px'}
          />
          <StyledFooter>
            <StyledFooterText>
              {`${displayedRowCount} ${displayedRowCount === 1 ? 'record' : 'records'}`}
            </StyledFooterText>
          </StyledFooter>
        </TableWrapper>
        <TableWrapper>
          <Table
            ref={gridRef}
            showHeader={true}
            height={'660px'}
            headerButtons={[
              {
                inputProps: {
                  label: '',
                  id: 'addMenu',
                  testId: 'searchMenuInput',
                  value: searchInputValue,
                  onChange: handleSearchMenuInputChange,
                  onKeyDown: handleSearchMenuInTree,
                  'aria-label': 'add menu',
                },
                buttonProps: {
                  testId: 'addMenuButton',
                  label: 'Add Menu',
                  onClick: handleAddMenuClick,
                },
              },
            ]}
            headerTitle='Selected Menus'
            columnDefs={[
              {
                minWidth: 70,
                maxWidth: 70,
                field: '',
                headerName: 'Select',
                checkboxSelection: true,
                floatingFilter: false,
                sortable: false,
                flex: 1,
              },
              { minWidth: 120, maxWidth: 220, field: 'parents.store', headerName: 'MxRef', flex: 1 },
              { minWidth: 320, field: 'id', headerName: 'Menu', flex: 1, resizable: false },
            ]}
            rowData={selectedMenus}
            onRowSelected={handleRowSelected}
            rowSelection='multiple'
            returnedRecords={selectedMenus.length || 0}
          />
        </TableWrapper>
      </TableRowWrapper>
      <Buttons>
        <Button label='Back' variant={Variant.Secondary} onClick={() => handleBackClick()} disabled={false} />
        <Button
          label='Next'
          testId='NextButtonSelectMenus'
          onClick={() => setStep(ChangeSpecsStep.ReviewChanges)}
          disabled={selectedMenus?.length === 0}
        />
      </Buttons>
    </SelectMenusPage>
  );
};

export default SelectMenus;
